import React, { useState } from 'react';
import { Box, Button, Stack, CircularProgress, Menu, MenuItem, ListItemText, useTheme, Chip, Typography, ButtonGroup } from '@mui/material';
import { PurchaseOrderItem, PurchaseOrderItemStatus, PurchaseOrder } from '@domain/purchasing';
import { PurchasingView, usePurchasingUIState } from '../../../application/store';
import { GroupingOption } from '../types/types';
import PurchaseOrdersKanban from './views/kanban/PurchaseOrdersKanban';
import { PurchaseOrdersTable } from './views/table/PurchaseOrdersTable';
import PurchaseOrdersTimeline from './views/timeline/PurchaseOrdersTimeline';
import PurchaseOrderDraftsView from './views/drafts/PurchaseOrderDraftsView';
import { Paper } from '@shared/ui/common/paper';
import EmptyState from '@shared/ui/common/EmptyState';
import ErrorState from '@shared/ui/common/ErrorState';
import { usePurchaseOrderItems, usePurchasing } from '../hooks';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { CreatePurchaseOrderFlow } from './create/CreatePurchaseOrderFlow';
import { usePurchaseOrderFilter } from '@features/purchasing/hooks/usePurchaseOrderFilter';
import PurchaseOrderFilter from '@shared/ui/filters/PurchaseOrderFilter';
import { useProducts } from '@features/products/hooks/useProducts';
import { useShipments } from '@features/shipments/hooks/useShipments';
import { useSuppliers } from '@features/suppliers/hooks/useSuppliers';
import { groupByShipment, groupByProduct, groupByPurchaseOrder, groupBySupplier, getUngroupedItems } from '../utils/groupItems';
import { useMemo } from 'react';
import { Icon } from '@shared/icons';



const Purchasing = () => {
  const { currentView, setView, grouping, setGrouping } = usePurchasingUIState();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();

  // Get data for filter autocomplete
  const { products } = useProducts();
  const { shipments } = useShipments();
  const { suppliers } = useSuppliers();
  const { purchaseOrders } = usePurchasing();

  // Filter logic
  const {
    filterCriteria,
    updateFilter
  } = usePurchaseOrderFilter();

  // Get display names for selected filters
  const getSelectedPurchaseOrder = () => {
    if (!filterCriteria.purchaseOrderId) return null;
    const po = purchaseOrders?.find(p => p.id === filterCriteria.purchaseOrderId);
    if (!po) return null;
    const orderDate = po.orderDate ? new Date(po.orderDate).toLocaleDateString() : '';
    const poNumber = po.poNumber || 'No PO Number';
    return `${poNumber}${orderDate ? ` (${orderDate})` : ''}`;
  };

  const getSelectedProduct = () => {
    if (!filterCriteria.productId) return null;
    const product = products?.status === 'success' ? products.data.find(p => p.id === filterCriteria.productId) : null;
    return product?.title || 'Unknown Product';
  };

  const getSelectedShipment = () => {
    if (!filterCriteria.shipmentId) return null;
    const shipment = shipments?.find(s => s.id === filterCriteria.shipmentId);
    if (!shipment) return null;
    const processedDate = shipment.processedDate ? new Date(shipment.processedDate).toLocaleDateString() : '';
    const shipmentName = shipment.name || 'No Shipment Name';
    return `${shipmentName}${processedDate ? ` (${processedDate})` : ''}`;
  };

  const getSelectedSupplier = () => {
    if (!filterCriteria.supplierId) return null;
    const supplier = suppliers.find(s => s.id === filterCriteria.supplierId);
    return supplier?.name || 'Unknown supplier';
  };

  // Clear individual filters
  const clearPurchaseOrderFilter = () => {
    updateFilter({ ...filterCriteria, purchaseOrderId: undefined });
  };

  const clearProductFilter = () => {
    updateFilter({ ...filterCriteria, productId: undefined });
  };

  const clearShipmentFilter = () => {
    updateFilter({ ...filterCriteria, shipmentId: undefined });
  };

  const clearSupplierFilter = () => {
    updateFilter({ ...filterCriteria, supplierId: undefined });
  };


  // Get purchase order items with filters
  const { updatePurchaseOrderItemStatus, isLoadingItems, error, purchaseOrderItems } = usePurchaseOrderItems({
    filters: filterCriteria
  });

  // Group items locally based on selected grouping
  const { groupedItems, ungroupedItems } = useMemo(() => {
    if (!purchaseOrderItems) return { groupedItems: [], ungroupedItems: [] };

    let grouped: any[] = [];

    switch (grouping) {
      case GroupingOption.SHIPMENT:
        grouped = groupByShipment(purchaseOrderItems);
        break;
      case GroupingOption.PRODUCT:
        grouped = groupByProduct(purchaseOrderItems);
        break;
      case GroupingOption.PURCHASE_ORDER:
        grouped = groupByPurchaseOrder(purchaseOrderItems);
        break;
      case GroupingOption.SUPPLIER:
        grouped = groupBySupplier(purchaseOrderItems);
        break;
      case GroupingOption.NO_GROUPING:
        grouped = [];
        break;
    }

    const ungrouped = getUngroupedItems(purchaseOrderItems, grouping);

    return { groupedItems: grouped, ungroupedItems: ungrouped };
  }, [purchaseOrderItems, grouping]);

  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  // Count draft purchase orders
  const draftCount = purchaseOrders?.filter((po: PurchaseOrder) => po.draft === true).length || 0;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setView(newValue as PurchasingView);
  };



  const handleGroupingMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleGroupingMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleGroupingChange = (_event: React.MouseEvent<HTMLElement> | null, newGrouping: GroupingOption) => {
    setGrouping(newGrouping);
    handleGroupingMenuClose();
  };

  const handleCreatePurchaseOrder = () => {
    openBottomSheet({
      title: 'Purchase Order Draft',
      component: <CreatePurchaseOrderFlow handleClose={async () => { await closeBottomSheet(); }} />,
    });
  };



  const handleStatusChange = async (item: PurchaseOrderItem, newStatus: PurchaseOrderItemStatus) => {
    try {
      if (!item) throw new Error('Item not found');

      await updatePurchaseOrderItemStatus({ itemId: item.id!, status: newStatus });
    } catch (error) {
      console.error('Failed to update purchase order item status:', error);
    }
  };

  return (
    <>
      {/* Header with New Purchase Order Button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" py={2}>

        <Stack direction="row" justifyContent="flex-end" alignItems="center">

          <Stack direction="row" alignItems="center"
            sx={{
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: 1,
            }}>
            <ButtonGroup variant="outlined" color="info">
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => handleTabChange(null as any, 'drafts')}
                variant={currentView === 'drafts' ? 'contained' : 'outlined'}
              >
                Drafts ({draftCount})

              </Button>
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => handleTabChange(null as any, 'timeline')}
                variant={currentView === 'timeline' ? 'contained' : 'outlined'}
              >
                Timeline
              </Button>
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => handleTabChange(null as any, 'board')}
                variant={currentView === 'board' ? 'contained' : 'outlined'}
              >
                Board
              </Button>
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => handleTabChange(null as any, 'list')}
                variant={currentView === 'list' ? 'contained' : 'outlined'}
              >
                List
              </Button>
            </ButtonGroup>
          </Stack>

          {/* Grouping Toggle and Filter */}
          {currentView !== 'drafts' && (
            <Stack direction="row" alignItems="center" gap={2}>
              {/* Applied Filters Chips */}


              <Stack direction="row" spacing={1} gap={1} overflow='auto' p={1}>
                {getSelectedPurchaseOrder() && (
                  <Chip
                    label={<Typography variant="body2" maxWidth={150} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${getSelectedPurchaseOrder()}`}</Typography>}
                    onDelete={clearPurchaseOrderFilter}
                    size="small"
                  />
                )}
                {getSelectedProduct() && (
                  <Chip
                    label={<Typography variant="body2" maxWidth={150} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${getSelectedProduct()}`}</Typography>}
                    onDelete={clearProductFilter}
                    size="small"
                  />
                )}
                {getSelectedShipment() && (
                  <Chip
                    label={<Typography variant="body2" maxWidth={150} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${getSelectedShipment()}`}</Typography>}
                    onDelete={clearShipmentFilter}
                    size="small"
                  />
                )}
                {getSelectedSupplier() && (
                  <Chip
                    label={<Typography variant="body2" maxWidth={150} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${getSelectedSupplier()}`}</Typography>}
                    onDelete={clearSupplierFilter}
                    size="small"
                  />
                )}

              </Stack>

              {/* Filter Component */}
              <PurchaseOrderFilter
                onFilterChange={updateFilter}
                initialCriteria={filterCriteria}
                purchaseOrders={purchaseOrders}
                products={products?.status === 'success' ? products.data : []}
                shipments={shipments}
                suppliers={suppliers}
              />

              {/* Grouping Toggle */}
              <Box>
                <Button
                  id="grouping-button"
                  variant="text"
                  color="info"

                  onClick={handleGroupingMenuOpen}
                  sx={{ justifyContent: 'space-between' }}
                >

                  Group
                  {grouping === GroupingOption.PURCHASE_ORDER && ': Purchase Order'}
                  {grouping === GroupingOption.PRODUCT && ': Product'}
                  {grouping === GroupingOption.SHIPMENT && ': Shipment'}
                  {grouping === GroupingOption.SUPPLIER && ': Supplier'}
                  {grouping === GroupingOption.NO_GROUPING && ': None'}
                </Button>


                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={handleGroupingMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'grouping-button',
                  }}
                >
                  <MenuItem onClick={() => handleGroupingChange(null as any, GroupingOption.NO_GROUPING)}>
                    <ListItemText>No Grouping</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={() => handleGroupingChange(null as any, GroupingOption.PURCHASE_ORDER)}>
                    <ListItemText>Purchase Order</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleGroupingChange(null as any, GroupingOption.PRODUCT)}>
                    <ListItemText>Product</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleGroupingChange(null as any, GroupingOption.SHIPMENT)}>
                    <ListItemText>Shipment</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleGroupingChange(null as any, GroupingOption.SUPPLIER)}>
                    <ListItemText>Supplier</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Stack>
          )}


        </Stack>


        <Button
          variant="text"
          color="secondary"
          startIcon={<Icon icon="Plus01" />}
          onClick={handleCreatePurchaseOrder}
          sx={{
            whiteSpace: 'nowrap',
          }}
        >
          New Purchase Order
        </Button>
      </Stack>

      {currentView === 'drafts' ? (
        <PurchaseOrderDraftsView />
      ) : (
        isLoadingItems ? (
          <Paper sx={{ p: 2, minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Paper>
        ) :
          error ? (
            <Paper sx={{ overflow: 'hidden' }}>
              <ErrorState error={error} />
            </Paper>
          )
            : (!purchaseOrderItems || purchaseOrderItems.length === 0) ? (
              <Paper>
                <EmptyState
                  title='No purchase orders found'
                  description='Create your first purchase order to get started'
                  action={{
                    label: 'Create purchase order',
                    onClick: handleCreatePurchaseOrder
                  }}
                ></EmptyState>
              </Paper>
            ) : (
              <>
                {/* Purchase Orders Table */}
                {currentView === 'timeline' && (
                  <PurchaseOrdersTimeline
                    groupedItems={groupedItems}
                    ungroupedItems={ungroupedItems}
                  />
                )}

                {currentView === 'board' && (
                  <>
                    <PurchaseOrdersKanban
                      items={purchaseOrderItems}
                      group={grouping}
                      onStatusChange={handleStatusChange}
                    />
                  </>
                )}

                {currentView === 'list' && (
                  <PurchaseOrdersTable
                    groupedItems={groupedItems}
                    ungroupedItems={ungroupedItems}
                  />
                )}
              </>
            )
      )}
    </>
  );
};

export default Purchasing;
